import { PAYOUT_METHODS, type PayoutMethod } from '@/domain/payout-methods'

/**
 * Single owner of the settlement-*provider* vocabulary. Every provider is declared
 * once in {@link SETTLEMENT_PROVIDERS} with its human label, the partner
 * payout-methods eligible for its settlements, how it settles, and whether it can
 * produce a provider-upload CSV; every other export below derives from that
 * registry, so a new provider is a single required entry (the `satisfies` shape
 * forces a settlement mode to be declared) rather than four hand-maintained lists.
 *
 * These are settlement-provider enum values (PAYPAL / WISE / CONFIRMO / MANUAL),
 * distinct from the partner *payout-method* labels — those live in
 * `@/domain/payout-methods` (`METHOD_LABELS` / `methodLabel`).
 *
 * Mirrors the settlement contract the backend publishes in gto-wizard#2682 (check
 * it there, not against symbol names that rot silently here): PayPal is the only
 * auto-processed provider, every other is completed by hand via "Mark as Paid";
 * Wise accepts BOTH the WIRE and WISE bank rails; only providers with a CSV
 * builder (Wise, Confirmo) export a file; MANUAL accepts any method
 * (`PAYOUT_METHODS`, the payout-method SSOT) and has no CSV.
 *
 * `eligibleMethods` is typed `PayoutMethod`, so a method that is not a real payout
 * rail fails the build here rather than silently disabling a provider in the UI.
 */
type SettlementProviderConfig = {
  label: string
  eligibleMethods: readonly PayoutMethod[]
  settlementMode: 'auto' | 'manual'
  exportsCsv: boolean
  creatable: boolean
}

const SETTLEMENT_PROVIDERS = {
  PAYPAL: {
    label: 'PayPal',
    eligibleMethods: ['PAYPAL'],
    settlementMode: 'auto',
    exportsCsv: false,
    creatable: true,
  },
  WISE: {
    label: 'Wise',
    eligibleMethods: ['WISE', 'WIRE'],
    settlementMode: 'manual',
    exportsCsv: true,
    creatable: true,
  },
  CONFIRMO: {
    label: 'Crypto (Confirmo)',
    eligibleMethods: ['CRYPTO'],
    settlementMode: 'manual',
    exportsCsv: true,
    creatable: true,
  },
  MANUAL: {
    label: 'Manual',
    // Accepts any rail — an operator-assembled batch is not restricted to one method.
    eligibleMethods: PAYOUT_METHODS,
    settlementMode: 'manual',
    exportsCsv: false,
    // Not offered in the create flow: accepting every rail, it would draw from the
    // same pending pool as the three real providers and silently strand payouts
    // they should have settled. Assembled out of band, completed from the detail page.
    creatable: false,
  },
} as const satisfies Record<string, SettlementProviderConfig>

/** Registry order — the declaration order above, which is also the menu order. */
const providersMatching = (
  predicate: (config: SettlementProviderConfig) => boolean,
): readonly string[] =>
  Object.entries(SETTLEMENT_PROVIDERS)
    .filter(([, config]) => predicate(config))
    .map(([provider]) => provider)

const AUTO_PROCESS_PROVIDERS = new Set(providersMatching((c) => c.settlementMode === 'auto'))
const MANUAL_COMPLETION_PROVIDERS = new Set(providersMatching((c) => c.settlementMode === 'manual'))
const CSV_EXPORT_PROVIDERS = new Set(providersMatching((c) => c.exportsCsv))

// Null-prototype so an inherited member ('toString', 'constructor') can never be
// mistaken for a registered provider: on a plain object the `?? fallback` below
// would never fire and a Function would flow into JSX or into `.includes()`.
const fromRegistry = <T>(pick: (config: SettlementProviderConfig) => T): Record<string, T> =>
  Object.assign(
    Object.create(null) as Record<string, T>,
    Object.fromEntries(
      Object.entries(SETTLEMENT_PROVIDERS).map(([provider, config]) => [provider, pick(config)]),
    ),
  )

/** Human labels keyed by provider — derived from the registry for read views. */
export const PROVIDER_LABELS: Record<string, string> = fromRegistry((config) => config.label)

const PROVIDER_ELIGIBLE_METHODS: Record<string, readonly string[]> = fromRegistry(
  (config) => config.eligibleMethods,
)

/**
 * Partner payout-methods eligible for a provider's settlements (empty for an
 * unknown provider) — Wise returns both WISE and WIRE, the two bank rails its
 * batch builder consumes.
 */
export const providerEligibleMethods = (provider: string): readonly string[] =>
  PROVIDER_ELIGIBLE_METHODS[provider] ?? []

/**
 * The slice of `api.ts`'s `ChannelOverview` this module needs. Declared
 * structurally so the vocabulary registry stays a leaf — `api.ts` depends on
 * these labels, never the reverse.
 */
type PayoutChannelCount = {
  payout_method: string
  count: number
}

/**
 * How many pending payouts a provider could settle: the channel-overview counts
 * SUMMED across every method it accepts, not a single channel row. Wise must add
 * its WISE and WIRE channels together, or a partner base paying by Wire alone
 * leaves the provider looking empty and the operator unable to open a settlement.
 */
export const providerEligibleCount = (
  channels: readonly PayoutChannelCount[] | undefined,
  provider: string,
): number => {
  const methods = providerEligibleMethods(provider)

  return (channels ?? [])
    .filter((channel) => methods.includes(channel.payout_method))
    .reduce((sum, channel) => sum + channel.count, 0)
}

/**
 * Whether the registry knows this provider at all. A provider string the FE does
 * not recognise fails every action gate below, which would strand a READY
 * settlement with no way to complete it — callers report that instead of
 * silently rendering a dead end.
 */
export const isKnownSettlementProvider = (provider: string): boolean =>
  Object.hasOwn(SETTLEMENT_PROVIDERS, provider)

/** PayPal auto-processes via the provider API ("Pay Settlement"). */
export const isAutoProcessProvider = (provider: string): boolean =>
  AUTO_PROCESS_PROVIDERS.has(provider)

/** Wise/Confirmo/Manual are disbursed by hand and closed with "Mark as Paid". */
export const isManualCompletionProvider = (provider: string): boolean =>
  MANUAL_COMPLETION_PROVIDERS.has(provider)

/** Only Wise/Confirmo can produce a provider-upload CSV; the rest have no file. */
export const canExportSettlementCsv = (provider: string): boolean =>
  CSV_EXPORT_PROVIDERS.has(provider)

/**
 * The providers an operator can open a NEW settlement for, in registry order.
 * Derived, not hand-listed — a provider added to the registry reaches the create
 * menu by declaring `creatable`, so it can never be known everywhere else yet
 * silently missing here (the unknown-provider guard cannot catch that case,
 * precisely because such a provider IS known).
 */
export const CREATABLE_PROVIDERS = providersMatching((config) => config.creatable)
